import { ResponsiveLine } from "@nivo/line";
import { useEffect, useState } from "react";
import { getColour, getColourPaletteHex } from "../../../utils/colours";
import { randomIntFromInterval } from "../../../utils/random";

interface timeSeriesObservation {
  x: string;
  y: number;
}

interface lineData {
  id: string;
  data: timeSeriesObservation[];
}

const dummyData = [
  {
    id: "timeSeries",
    data: Array.from(Array(10).keys()).map((x) => ({
      x: x.toString(),
      y: randomIntFromInterval(1, 10),
    })),
  },
];

function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

export default function LineChart({
  data,
  colours,
  margins,
  showLegend,
  className,
}: {
  data: lineData[];
  colours?: string[] | null;
  margins?: { top: number; right: number; bottom: number; left: number } | null;
  showLegend?: boolean;
  className?: string;
}) {
  const [windowDimensions, setWindowDimensions] = useState(
    getWindowDimensions(),
  );

  useEffect(() => {
    function handleResize() {
      setWindowDimensions(getWindowDimensions());
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const validData = data.filter((x: any) => x.data.length > 0);
  const showPlaceholder = validData.length === 0;
  const dataPoints = showPlaceholder ? 0 : validData[0].data.length;

  let widthClass = "h-full";
  if (windowDimensions.width < 992) {
    if (dataPoints > 3 && dataPoints <= 5) {
      widthClass = "h-full w-[32rem]";
    } else if (dataPoints > 5 && dataPoints <= 10) {
      widthClass = "h-full w-[40rem]";
    } else if (dataPoints > 10) {
      widthClass = "h-full w-[50rem]";
    }
  }

  return (
    <div className={className}>
      <div className={widthClass}>
        <ResponsiveLine
          animate
          areaOpacity={0.1}
          axisBottom={null}
          // axisBottom={showPlaceholder ? null : {
          //   format: "%d %b %H:%M",
          //   tickValues: "every 1 hours",
          //   legendOffset: -12,
          //   tickRotation: -45,
          //   tickSize: 0,
          //   tickPadding: 10,
          // }}
          axisLeft={
            showPlaceholder
              ? null
              : {
                  tickSize: 0,
                  tickPadding: 10,
                  tickRotation: 0,
                }
          }
          colors={
            showPlaceholder
              ? getColour("placeholder")
              : colours !== null
              ? colours
              : [getColourPaletteHex("light-info")]
          }
          curve="monotoneX"
          data={showPlaceholder ? dummyData : validData}
          enableArea={true}
          enableGridX={false}
          enableGridY={!showPlaceholder}
          enablePointLabel={false}
          enableSlices={false}
          isInteractive={!showPlaceholder}
          lineWidth={3}
          legends={
            showLegend === true
              ? [
                  {
                    anchor: "bottom-right",
                    direction: "column",
                    justify: false,
                    translateX: 100,
                    translateY: 0,
                    itemsSpacing: 0,
                    itemDirection: "left-to-right",
                    itemWidth: 80,
                    itemHeight: 20,
                    itemOpacity: 0.75,
                    symbolSize: 12,
                    symbolShape: "circle",
                    symbolBorderColor: "rgba(0, 0, 0, .5)",
                  },
                ]
              : []
          }
          margin={
            showPlaceholder
              ? { top: 20, right: 20, bottom: 20, left: 20 }
              : margins !== null
              ? margins
              : { top: 20, right: 40, bottom: 20, left: 40 }
          }
          motionConfig="wobbly"
          pointBorderColor={{ from: "serieColor" }}
          pointBorderWidth={2}
          pointSize={10}
          useMesh
          xFormat={showPlaceholder ? "" : "time:%d %b %H:%M"}
          xScale={
            showPlaceholder
              ? { type: "point" }
              : {
                  format: "%Y-%m-%dT%H:%M:%SZ",
                  precision: "hour",
                  type: "time",
                  useUTC: false,
                }
          }
          yFormat=" >-.0f"
          yScale={{
            type: "linear",
            min: 0,
            max: "auto",
            stacked: false,
            reverse: false,
          }}
        />
      </div>
    </div>
  );
}

LineChart.defaultProps = {
  className: "",
  colours: null,
  margins: null,
  showLegend: false,
};
